import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import './CaseStudyResources.css';

const PDF_MARGIN = 18;

function addWrappedText(pdf, value, options) {
  const { x, y, width, fontSize, lineHeight, color, bold = false } = options;
  const text = value.replace(/\s+/g, ' ').trim();
  if (!text) return y;

  pdf.setFont('helvetica', bold ? 'bold' : 'normal');
  pdf.setFontSize(fontSize);
  pdf.setTextColor(...color);
  const lines = pdf.splitTextToSize(text, width);
  const pageHeight = pdf.internal.pageSize.getHeight();
  let cursorY = y;

  lines.forEach((line) => {
    if (cursorY + lineHeight > pageHeight - 20) {
      pdf.addPage();
      cursorY = PDF_MARGIN;
    }
    pdf.text(line, x, cursorY);
    cursorY += lineHeight;
  });

  return cursorY;
}

function createCaseStudyPdf(title) {
  const article = document.querySelector('.cs-article');
  const pageTitle = document.querySelector('.cs-title')?.textContent.trim() || title;
  const summary = document.querySelector('.cs-summary')?.textContent.trim() || '';

  if (!article) {
    throw new Error('We could not prepare this case study. Please refresh and try again.');
  }

  const pdf = new jsPDF({ unit: 'mm', format: 'a4', compress: true });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const contentWidth = pageWidth - PDF_MARGIN * 2;
  let y = 20;

  pdf.setFillColor(33, 132, 91);
  pdf.roundedRect(PDF_MARGIN, y - 7, 2, 25, 1, 1, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.setTextColor(33, 132, 91);
  pdf.text('AIVION  /  CASE STUDY', PDF_MARGIN + 8, y);
  y += 13;

  y = addWrappedText(pdf, pageTitle, {
    x: PDF_MARGIN + 8, y, width: contentWidth - 8,
    fontSize: 21, lineHeight: 9, color: [23, 59, 93], bold: true
  });
  y += 2;
  y = addWrappedText(pdf, summary, {
    x: PDF_MARGIN + 8, y, width: contentWidth - 8,
    fontSize: 10, lineHeight: 5, color: [87, 101, 119]
  });
  y += 7;
  pdf.setDrawColor(220, 226, 219);
  pdf.line(PDF_MARGIN, y, pageWidth - PDF_MARGIN, y);
  y += 10;

  article.querySelectorAll('h2, p, li').forEach((element) => {
    const text = element.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return;

    const isHeading = element.tagName === 'H2';
    y = addWrappedText(pdf, text, {
      x: PDF_MARGIN,
      y,
      width: contentWidth,
      fontSize: isHeading ? 13 : 10,
      lineHeight: isHeading ? 7 : 5,
      color: isHeading ? [23, 107, 75] : [48, 58, 70],
      bold: isHeading
    });
    y += isHeading ? 2 : 3;
  });

  const pageCount = pdf.internal.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    pdf.setPage(page);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(120, 130, 141);
    pdf.text('AIVION  |  Delivering meaningful outcomes through intelligent AI solutions', PDF_MARGIN, 288);
    pdf.text(`${page} / ${pageCount}`, pageWidth - PDF_MARGIN, 288, { align: 'right' });
  }

  return pdf.output('datauristring').split(',')[1];
}

const CaseStudyResources = ({ title }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [formData, setFormData] = useState({ name: '', email: '', company: '' });

  const handleChange = (event) => {
    setFormData((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSending(true);
    setStatus({ type: '', message: '' });

    try {
      const pdfBase64 = createCaseStudyPdf(title);
      const response = await fetch('/api/sendEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'caseStudyPdf',
          ...formData,
          title: document.querySelector('.cs-title')?.textContent.trim() || title,
          pdfBase64
        })
      });
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.toLowerCase().includes('json')) {
        const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
        throw new Error(isLocal
          ? 'The local email API is not running. Start Azure Functions in a second terminal with “npm run start:api”, then try again.'
          : 'The case study email service is unavailable right now. Please try again later or contact info@aivion.ai.');
      }

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'We could not send the PDF. Please try again.');

      setStatus({ type: 'success', message: `The case study PDF has been sent to ${formData.email}. Please check your inbox.` });
      setFormData({ name: '', email: '', company: '' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Unable to send the PDF. Please try again later.' });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section className="case-study-resources" aria-labelledby="case-study-resource-title">
      <div className="case-study-resources__intro">
        <p className="case-study-resources__eyebrow">TAKE THIS CASE STUDY WITH YOU</p>
        <h3 id="case-study-resource-title">{title}</h3>
        <p>Enter your details and we’ll email you a print-ready PDF copy.</p>
      </div>
      <div className="case-study-resources__actions">
        <button
          type="button"
          onClick={() => {
            setIsFormOpen((open) => !open);
            setStatus({ type: '', message: '' });
          }}
          aria-expanded={isFormOpen}
        >
          {isFormOpen ? 'Close form' : 'Get PDF by email'}
        </button>
      </div>
      {isFormOpen && (
        <div className="case-study-pdf-modal-backdrop" onMouseDown={(event) => {
          if (event.target === event.currentTarget && !isSending) setIsFormOpen(false);
        }}>
          <section className="case-study-pdf-modal" role="dialog" aria-modal="true" aria-labelledby="case-study-modal-title">
            <button className="case-study-pdf-modal__close" type="button" onClick={() => setIsFormOpen(false)} aria-label="Close form" disabled={isSending}>×</button>
            <p className="case-study-resources__eyebrow">TAKE THIS CASE STUDY WITH YOU</p>
            <h3 id="case-study-modal-title">{title}</h3>
            <p className="case-study-pdf-modal__description">Enter your details and we’ll email you a print-ready PDF copy.</p>
        <form className="case-study-pdf-form" onSubmit={handleSubmit}>
          <label>
            Name
            <input name="name" value={formData.name} onChange={handleChange} autoComplete="name" required maxLength={120} />
          </label>
          <label>
            Email address
            <input type="email" name="email" value={formData.email} onChange={handleChange} autoComplete="email" required maxLength={254} />
          </label>
          <label>
            Company name
            <input name="company" value={formData.company} onChange={handleChange} autoComplete="organization" required maxLength={160} />
          </label>
          <p className="case-study-pdf-form__privacy">We’ll use these details only to send the requested case study and follow up on your request.</p>
          <button type="submit" disabled={isSending}>
            {isSending ? 'Sending PDF…' : 'Email me the PDF'}
          </button>
          {status.message && <p className={`case-study-pdf-form__status is-${status.type}`} role="status">{status.message}</p>}
        </form>
          </section>
        </div>
      )}
    </section>
  );
};

export default CaseStudyResources;
